import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMaskDirective } from 'ngx-mask';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '../icon/icon.component';
import { GetCountriesService } from '@app/pages/home-page/services/get-countries.service';
import { COUNTRY_NAME_TO_CODE_MAP } from '@app/utils/constants/country-mapping.constants';
import { Country, CountriesList } from '@app/utils/interfaces/countries-list';
import { IconName } from '@app/utils/enums/icon-enum';
import { MaskOptions } from '@app/utils/interfaces/mask.interface';

@Component({
  selector: 'app-phone-number-form-field',
  standalone: true,
  imports: [TranslateModule, NgxMaskDirective, ReactiveFormsModule, IconComponent],
  templateUrl: './phone-number-form-field.component.html',
  styleUrl: './phone-number-form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneNumberFormFieldComponent implements OnInit {
  public country = input.required<Country | null>();
  public control = input.required<FormControl<string>>();
  public labelText = input.required<string>();
  public phoneMaskOptions = input.required<MaskOptions>();
  public errorDescriptions = input<Map<string, string>>(new Map());
  public countryChanged = output<Country>();

  protected shortInfoText = computed(() => {
    const country = this.country();

    if (country) {
      return `${COUNTRY_NAME_TO_CODE_MAP[country.name]} (${country.phoneCode})`;
    }

    return '';
  });
  protected countries = signal<Country[]>([]);
  protected isSelectorDisplayed = signal(false);

  protected iconName = IconName;

  private readonly countriesService = inject(GetCountriesService);
  private readonly destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.countriesService
      .getCountries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ data: countries }: CountriesList) => {
          this.countries.set(countries);
        },
      });
  }

  protected changeCountry(selector: Event): void {
    const target = selector.target as HTMLElement;

    const itemElement = target.closest('[data-item-index]') as HTMLDivElement;

    if (!itemElement) {
      return;
    }

    const selectedIndex = Number(itemElement.dataset['itemIndex']);
    const selectedCountry = this.countries()[selectedIndex];

    if (selectedCountry.name !== this.country()?.name) {
      this.countryChanged.emit(selectedCountry);
    }

    this.isSelectorDisplayed.set(false);
  }

  protected tryGetFirstErrorKey(): string {
    const errors = this.control().errors;
    if (errors) {
      const firstErrorKey = Object.keys(errors)[0];

      return this.errorDescriptions().get(firstErrorKey)!;
    }

    return '';
  }
}
